'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '../context/AuthContext';
import { Navbar, Footer } from '../../components/Layout';
import { 
  User, Mail, Phone, MapPin, CheckCircle, AlertCircle, Plus, 
  Trash2, Star, ShieldCheck, HelpCircle, Loader2, Sparkles,
  Clock, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function ProfilePage() {
  const { token, isAuthenticated } = useAuth();
  const router = useRouter();

  const [profile, setProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Editable Profile Fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [foodPreference, setFoodPreference] = useState('VEG');

  // Address Form
  const [newAddress, setNewAddress] = useState('');
  const [addressError, setAddressError] = useState('');

  // OTP Verification state
  const [otpMode, setOtpMode] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [sentOtpCode, setSentOtpCode] = useState(''); // dummy code returned

  const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    fetchProfile();
  }, [token]);

  const fetchProfile = async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch(`${authApi}/api/users/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setFirstName(data.firstName || '');
        setLastName(data.lastName || '');
        setEmail(data.email || '');
        setMobileNumber(data.mobileNumber || '');
        setFoodPreference(data.foodPreference || 'VEG');
      } else {
        setError('Failed to fetch profile details.');
      }
    } catch (err) {
      console.error(err);
      setError('Connection failure with authentication service.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${authApi}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email, mobileNumber, foodPreference, firstName, lastName })
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setSuccess('Profile updated successfully!');
        setIsEditModalOpen(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const msg = await res.text();
        setError(msg || 'Failed to update profile.');
      }
    } catch (err) {
      console.error(err);
      setError('Connection failure during profile update.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddressError('');
    if (!newAddress.trim()) return;

    if (profile?.addresses && profile.addresses.length >= 5) {
      setAddressError('Limit reached. You can only save up to 5 addresses.');
      return;
    }

    try {
      const res = await fetch(`${authApi}/api/users/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ address: newAddress.trim() })
      });

      if (res.ok) {
        const data = await res.json();
        setProfile((prev: any) => ({
          ...prev,
          addresses: data.addresses,
          primaryAddress: data.primaryAddress
        }));
        setNewAddress('');
      } else {
        const msg = await res.text();
        setAddressError(msg || 'Failed to add address.');
      }
    } catch (err) {
      console.error(err);
      setAddressError('Connection failure while adding address.');
    }
  };

  const handleDeleteAddress = async (addressToDelete: string) => {
    setAddressError('');
    try {
      const res = await fetch(`${authApi}/api/users/addresses?address=${encodeURIComponent(addressToDelete)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        const data = await res.json();
        setProfile((prev: any) => ({
          ...prev,
          addresses: data.addresses,
          primaryAddress: data.primaryAddress
        }));
      } else {
        setAddressError('Failed to delete address.');
      }
    } catch (err) {
      console.error(err);
      setAddressError('Connection failure while deleting address.');
    }
  };

  const handleSetPrimaryAddress = async (addressToSet: string) => {
    setAddressError('');
    try {
      const res = await fetch(`${authApi}/api/users/addresses/primary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ address: addressToSet })
      });

      if (res.ok) {
        const data = await res.json();
        setProfile((prev: any) => ({
          ...prev,
          primaryAddress: data.primaryAddress
        }));
      } else {
        setAddressError('Failed to set primary address.');
      }
    } catch (err) {
      console.error(err);
      setAddressError('Connection failure while setting primary address.');
    }
  };

  const triggerSendOtp = async () => {
    setOtpError('');
    if (!mobileNumber.trim()) {
      setOtpError('Please enter and save a valid mobile number first.');
      return;
    }

    try {
      const res = await fetch(`${authApi}/api/users/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ mobileNumber })
      });

      if (res.ok) {
        const data = await res.json();
        setSentOtpCode(data.otp || '');
        setOtpMode(true);
      } else {
        setOtpError('Failed to send verification OTP.');
      }
    } catch (err) {
      console.error(err);
      setOtpError('Connection failure with OTP service.');
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    try {
      const res = await fetch(`${authApi}/api/users/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ otp: otpValue })
      });

      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setOtpSuccess(true);
        setOtpMode(false);
        setOtpValue('');
        setTimeout(() => setOtpSuccess(false), 4000);
      } else {
        setOtpError('Incorrect OTP code. Try again.');
      }
    } catch (err) {
      console.error(err);
      setOtpError('Connection failure during OTP verification.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF5] flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center py-24">
          <Loader2 className="w-12 h-12 text-mid-green animate-spin mb-4" />
          <span className="text-text-muted font-bold text-sm">Loading your profile deck...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated || !profile) {
    return (
      <div className="min-h-screen bg-[#FAFAF5] flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center py-24 px-6 text-center">
          <AlertCircle className="w-16 h-16 text-amber mb-6" />
          <h1 className="text-3xl font-display font-black text-deep-green mb-3">Access Restricted</h1>
          <p className="text-text-muted font-medium mb-8 max-w-sm">Please log in to manage your address book, profile details, and mobile verification status.</p>
          <button 
            onClick={() => router.push('/login')}
            className="px-8 py-3.5 bg-mid-green text-white font-bold rounded-xl hover:bg-deep-green transition-all shadow-md uppercase tracking-wider text-xs"
          >
            Go to Login
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9F5] flex flex-col font-body selection:bg-amber/30 selection:text-deep-green">
      <Navbar />

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6 sm:px-6 sm:py-12">
        {/* Banner Section */}
        <div className="bg-deep-green rounded-none sm:rounded-[2.5rem] p-4 sm:p-12 text-white shadow-none sm:shadow-xl relative overflow-hidden mb-6 sm:mb-10 text-left">
          <div className="absolute top-0 right-0 w-80 h-80 bg-mid-green/10 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber/10 rounded-full blur-3xl -z-10" />
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <span className="text-amber font-bold text-xs uppercase tracking-widest flex items-center gap-1.5 mb-2">
                <Sparkles className="w-4 h-4" /> Premium Harvest Patron
              </span>
              <h1 className="text-3xl md:text-5xl font-display font-black">
                Welcome, {profile.firstName ? `${profile.firstName} ${profile.lastName}`.trim() : profile.username}!
              </h1>
              <p className="text-white/70 text-sm mt-2 font-medium">Manage your personal settings, addresses, and details in one place.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10 flex items-center gap-3">
              <div className="w-12 h-12 bg-amber text-deep-green rounded-full flex items-center justify-center font-bold text-lg shadow-inner">
                {profile.firstName ? `${profile.firstName.substring(0, 1)}${profile.lastName ? profile.lastName.substring(0, 1) : ''}`.toUpperCase() : profile.username.substring(0, 2).toUpperCase()}
              </div>
              <div className="text-left">
                <span className="block text-xs text-white/60">Preferred diet</span>
                <span className="text-sm font-bold text-amber">{profile.foodPreference === 'VEG' ? 'Pure Vegetarian 🌱' : 'Non-Vegetarian 🍗'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Feedback alerts */}
        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-2xl text-sm font-semibold mb-6 flex items-center gap-3 shadow-sm text-left">
            <CheckCircle className="w-5 h-5 shrink-0" />
            {success}
          </div>
        )}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-2xl text-sm font-medium mb-6 flex items-center gap-3 shadow-sm text-left">
            <AlertCircle className="w-5 h-5 shrink-0" />
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile form */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-transparent sm:bg-white p-6 sm:p-8 rounded-none sm:rounded-[2.5rem] border-0 sm:border border-border/50 shadow-none sm:shadow-md text-left">
              <div className="flex justify-between items-center border-b border-border/40 pb-4 mb-6">
                <h2 className="text-xl font-bold text-deep-green flex items-center gap-2">
                  <User className="w-5 h-5 text-amber" /> Personal Information
                </h2>
                <button 
                  onClick={() => setIsEditModalOpen(true)}
                  className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-mid-green hover:text-deep-green border border-mid-green/30 hover:border-deep-green px-3.5 py-1.5 rounded-xl transition-all"
                >
                  Edit Details
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-surface p-4 rounded-2xl border border-border/40">
                  <span className="block text-[10px] font-bold text-text-muted uppercase tracking-widest mb-1">First Name</span>
                  <span className="text-sm font-black text-deep-green">{profile.firstName || 'Not Configured'}</span>
                </div>
                <div className="bg-surface p-4 rounded-2xl border border-border/40">
                  <span className="block text-[10px] font-bold text-text-muted uppercase tracking-widest mb-1">Last Name</span>
                  <span className="text-sm font-black text-deep-green">{profile.lastName || 'Not Configured'}</span>
                </div>
                <div className="bg-surface p-4 rounded-2xl border border-border/40">
                  <span className="block text-[10px] font-bold text-text-muted uppercase tracking-widest mb-1">Email Address</span>
                  <span className="text-sm font-black text-deep-green">{profile.email || 'Not Configured'}</span>
                </div>
                <div className="bg-surface p-4 rounded-2xl border border-border/40">
                  <span className="block text-[10px] font-bold text-text-muted uppercase tracking-widest mb-1">Mobile Number</span>
                  <span className="text-sm font-black text-deep-green">{profile.mobileNumber || 'Not Configured'}</span>
                </div>
                <div className="bg-surface p-4 rounded-2xl border border-border/40 col-span-2">
                  <span className="block text-[10px] font-bold text-text-muted uppercase tracking-widest mb-1">Food Preference</span>
                  <span className="text-sm font-black text-deep-green">{profile.foodPreference === 'VEG' ? 'Pure Vegetarian 🌱' : 'Non-Vegetarian 🍗'}</span>
                </div>
              </div>

              {/* Edit Profile Modal */}
              <AnimatePresence>
                {isEditModalOpen && (
                  <div className="fixed inset-0 bg-black/65 backdrop-blur-sm z-[150] flex items-center justify-center p-4 text-left">
                    <div className="absolute inset-0" onClick={() => setIsEditModalOpen(false)} />
                    
                    <motion.div
                      initial={{ scale: 0.95, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.95, opacity: 0 }}
                      className="bg-white w-full max-w-lg rounded-[2rem] p-6 sm:p-8 shadow-2xl relative z-10 border border-border/60 max-h-[90vh] overflow-y-auto"
                    >
                      <div className="flex justify-between items-center pb-4 mb-6 border-b border-border/40">
                        <h3 className="font-display font-black text-lg text-deep-green uppercase tracking-wide">Edit Details</h3>
                        <button 
                          type="button"
                          onClick={() => setIsEditModalOpen(false)}
                          className="p-1.5 rounded-full hover:bg-surface border border-border"
                        >
                          <X className="w-4 h-4 text-deep-green" />
                        </button>
                      </div>

                      <form onSubmit={handleUpdateProfile} className="space-y-5">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">First Name</label>
                            <input 
                              type="text" 
                              required
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              className="w-full px-4 py-3 bg-white border border-border focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none rounded-xl font-medium text-sm text-deep-green"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Last Name</label>
                            <input 
                              type="text" 
                              required
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              className="w-full px-4 py-3 bg-white border border-border focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none rounded-xl font-medium text-sm text-deep-green"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Email Address</label>
                          <div className="relative">
                            <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-text-muted" />
                            <input 
                              type="email" 
                              required
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="w-full pl-11 pr-4 py-3 bg-white border border-border focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none rounded-xl font-medium text-sm text-deep-green"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Mobile Number</label>
                          <div className="relative">
                            <Phone className="absolute left-3.5 top-3.5 w-4 h-4 text-text-muted" />
                            <input 
                              type="text" 
                              required
                              placeholder="+91 XXXXX XXXXX"
                              value={mobileNumber}
                              onChange={(e) => setMobileNumber(e.target.value)}
                              className="w-full pl-11 pr-4 py-3 bg-white border border-border focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none rounded-xl font-medium text-sm text-deep-green"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-2">Food Preference</label>
                          <select 
                            value={foodPreference}
                            onChange={(e) => setFoodPreference(e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-border focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none rounded-xl font-bold text-gray-700 text-sm"
                          >
                            <option value="VEG">Pure Vegetarian 🌱</option>
                            <option value="NON_VEG">Non-Vegetarian 🍗</option>
                          </select>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-border/40 mt-6">
                          <button
                            type="button"
                            onClick={() => setIsEditModalOpen(false)}
                            className="px-5 py-2.5 bg-pale-green hover:bg-amber hover:text-white text-deep-green font-bold rounded-xl text-xs uppercase tracking-wider transition-all"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={isSaving}
                            className="bg-mid-green hover:bg-deep-green text-white font-bold px-6 py-2.5 rounded-xl transition-all text-xs uppercase tracking-wider shadow-md hover:shadow-lg flex items-center gap-2"
                          >
                            {isSaving ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                              </>
                            ) : (
                              'Save Changes'
                            )}
                          </button>
                        </div>
                      </form>
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile Verification Details */}
            <div className="bg-transparent sm:bg-white p-0 sm:p-8 rounded-none sm:rounded-[2.5rem] border-0 sm:border border-border/50 shadow-none sm:shadow-md text-left">
              <h2 className="text-xl font-bold text-deep-green flex items-center gap-2 border-b border-border/40 pb-4 mb-6">
                <ShieldCheck className="w-5 h-5 text-amber" /> Security & OTP Verification
              </h2>

              {otpSuccess && (
                <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-xl text-sm font-semibold mb-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 shrink-0" />
                  Mobile number verified successfully!
                </div>
              )}

              {otpError && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-xl text-sm font-medium mb-4 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  {otpError}
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-2xl border border-border/60 bg-surface">
                <div className="space-y-1">
                  <span className="text-xs font-bold text-text-muted uppercase tracking-wider block">Verification status</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-deep-green">{mobileNumber || 'No mobile number configured'}</span>
                    {profile.mobileVerified ? (
                      <span className="bg-green-100 text-green-800 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-800" /> Verified
                      </span>
                    ) : (
                      <span className="bg-amber/15 text-amber border border-amber/30 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                        Pending Verification
                      </span>
                    )}
                  </div>
                </div>

                {!profile.mobileVerified && mobileNumber && (
                  <button
                    onClick={triggerSendOtp}
                    className="bg-amber text-deep-green hover:bg-deep-green hover:text-white font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-sm"
                  >
                    Verify via OTP
                  </button>
                )}
              </div>

              {/* OTP modal/panel */}
              {otpMode && (
                <form onSubmit={handleVerifyOtp} className="mt-6 p-6 rounded-2xl border border-amber/25 bg-amber/5 animate-scale-up space-y-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber animate-pulse" />
                    <span className="font-bold text-deep-green text-sm">Enter OTP sent to {mobileNumber}</span>
                  </div>
                  {sentOtpCode ? (
                    <p className="text-xs text-text-muted font-medium">For testing, use dummy code <span className="font-bold text-amber bg-white px-2 py-0.5 rounded border border-amber/20">{sentOtpCode}</span></p>
                  ) : null}
                  
                  <div className="flex gap-3">
                    <input 
                      type="text"
                      required
                      placeholder="Enter 6-digit OTP"
                      value={otpValue}
                      onChange={(e) => setOtpValue(e.target.value)}
                      maxLength={6}
                      className="px-4 py-3 bg-white border border-border focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none rounded-xl font-bold text-sm tracking-widest text-center w-48 text-deep-green"
                    />
                    <button
                      type="submit"
                      className="bg-mid-green hover:bg-deep-green text-white font-bold px-6 py-3 rounded-xl text-xs uppercase tracking-wider transition-all shadow-md shrink-0"
                    >
                      Verify OTP
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Address Book Card */}
          <div className="bg-transparent sm:bg-white p-0 sm:p-8 rounded-none sm:rounded-[2.5rem] border-0 sm:border border-border/50 shadow-none sm:shadow-md text-left self-start">
            <h2 className="text-xl font-bold text-deep-green flex items-center gap-2 border-b border-border/40 pb-4 mb-6">
              <MapPin className="w-5 h-5 text-amber" /> Address Book
            </h2>

            {addressError && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3.5 rounded-xl text-xs font-semibold mb-4">
                {addressError}
              </div>
            )}

            {/* List addresses */}
            <div className="space-y-4 mb-6">
              {profile.addresses && profile.addresses.length > 0 ? (
                profile.addresses.map((addr: string, idx: number) => {
                  const isPrimary = profile.primaryAddress === addr;
                  const parts = addr.split('|');
                  let displayAddr = '';
                  let displayName = '';
                  if (parts.length >= 4) {
                    displayName = parts[0].trim();
                    displayAddr = parts[1].trim();
                  } else if (parts.length === 3) {
                    displayAddr = parts[0].trim();
                  } else {
                    displayAddr = addr.trim();
                  }
                  return (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-2xl border transition-all relative flex flex-col gap-2 ${
                        isPrimary 
                          ? 'border-amber bg-amber/5 shadow-md shadow-amber/5' 
                          : 'border-border/60 hover:border-mid-green'
                      }`}
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 text-left">
                          {displayName && (
                            <span className="inline-block bg-amber/10 text-amber text-[9px] font-black px-2 py-0.5 rounded border border-amber/20 mb-1.5 uppercase tracking-wider">
                              👤 {displayName}
                            </span>
                          )}
                          <p className="text-xs font-semibold text-deep-green leading-relaxed">{displayAddr}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteAddress(addr)}
                          className="text-red-400 hover:text-red-600 transition-colors p-1"
                          title="Delete address"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      <div className="flex justify-between items-center mt-1 border-t border-border/30 pt-2">
                        {isPrimary ? (
                          <span className="text-[10px] font-black text-amber uppercase tracking-wider flex items-center gap-1">
                            <Star className="w-3 h-3 text-amber fill-amber" /> Primary
                          </span>
                        ) : (
                          <button
                            onClick={() => handleSetPrimaryAddress(addr)}
                            className="text-[10px] font-bold text-mid-green hover:underline uppercase tracking-wider"
                          >
                            Set as primary
                          </button>
                        )}
                        <span className="text-[10px] text-text-muted font-bold">Address #{idx + 1}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-6 border border-dashed border-border/80 rounded-2xl text-text-muted text-xs font-semibold bg-surface">
                  No saved addresses found
                </div>
              )}
            </div>

            {/* Add Address Form */}
            {(!profile.addresses || profile.addresses.length < 5) && (
              <form onSubmit={handleAddAddress} className="space-y-3 pt-4 border-t border-border/40">
                <span className="block text-xs font-bold text-deep-green uppercase tracking-wider">Add New Address</span>
                <textarea
                  required
                  placeholder="Street, City, Pincode (e.g. 12 Organic St, Erode - 638001)"
                  value={newAddress}
                  onChange={(e) => setNewAddress(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-border focus:ring-2 focus:ring-mid-green focus:border-mid-green transition-all outline-none rounded-xl font-medium text-xs text-deep-green resize-none h-20"
                />
                <button
                  type="submit"
                  className="w-full bg-mid-green hover:bg-deep-green text-white font-bold py-3 rounded-xl text-xs uppercase tracking-wider transition-all shadow-md flex items-center justify-center gap-1.5"
                >
                  <Plus className="w-4 h-4" /> Save Address
                </button>
              </form>
            )}

            <div className="mt-4 text-[10px] text-text-muted font-medium text-center">
              Maximum 5 addresses allowed in address book.
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
